import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {apiService} from '../services/api';
import {HomeWidget, HomeWidgetLayoutItem} from '../types';

const KEY = ['homeWidgets'];

/**
 * Statistic widgets and screens the signed-in user pinned to their home page
 * (stored server-side per user). Every mutation answers with the full list,
 * ordered by grid position; a pin is addressed by its own id.
 */
export const useHomeWidgets = () => {
    const queryClient = useQueryClient();

    const query = useQuery({
        queryKey: KEY,
        queryFn: async (): Promise<HomeWidget[]> => {
            const response = await apiService.getHomeWidgets();
            if (!response.success || !Array.isArray(response.data)) {
                throw new Error(response.message || 'Widgety sa nepodarilo načítať.');
            }
            return response.data;
        },
    });

    const store = (response: {success: boolean; data?: HomeWidget[]; message?: string}) => {
        if (!response.success || !Array.isArray(response.data)) {
            throw new Error(response.message || 'Zmena widgetov sa nepodarila.');
        }
        queryClient.setQueryData(KEY, response.data);
    };

    const add = useMutation({mutationFn: async (source: {widgetId: string} | {screenId: string}) => store(await apiService.addHomeWidget(source))});
    const remove = useMutation({mutationFn: async (id: string) => store(await apiService.removeHomeWidget(id))});
    // Optimistic: the grid keeps what was dropped/resized while it saves; the answer replaces the list
    const layout = useMutation({
        mutationFn: async (items: HomeWidgetLayoutItem[]) => store(await apiService.saveHomeWidgetLayout(items)),
        onMutate: (items) => {
            const previous = queryClient.getQueryData<HomeWidget[]>(KEY);
            const byId = new Map(items.map((item) => [item.id, item]));
            queryClient.setQueryData<HomeWidget[]>(KEY, (list) => (list ?? []).map((widget) => ({...widget, ...byId.get(widget.id)})));
            return {previous};
        },
        onError: (_error, _vars, context) => {
            if (context?.previous) queryClient.setQueryData(KEY, context.previous);
        },
    });

    const widgets = query.data ?? [];
    return {
        widgets,
        isLoading: query.isLoading,
        error: query.error as Error | null,
        pin: add.mutateAsync,
        unpin: remove.mutateAsync,
        saveLayout: layout.mutate,
        isSaving: add.isPending || remove.isPending || layout.isPending,
    };
};
